/**
 * Token de passagem: login entre o portal e a loja sem JWT na URL.
 *
 * Como funciona a chegada de um usuário vindo do portal:
 *
 * 1. O portal chama `POST /api/Passage/Issue` e recebe um token curto, de uso
 *    único, que vive só na memória da API por 20 a 25 segundos.
 * 2. O portal abre a loja com `?message=redirect.token.lp&data=<token curto>`
 *    (ou com os mesmos parâmetros no fragmento `#message=...&data=...`).
 * 3. Um script inline no `index.html`, que roda antes do Google Analytics, do
 *    Meta Pixel e do Clarity, guarda `message` e `data` no `sessionStorage`
 *    (chave {@link PASSAGE_ARRIVAL_STORAGE_KEY}) e tira os dois da URL com
 *    `history.replaceState`. Assim nenhum script de terceiros vê o token.
 * 4. O React lê a chegada com {@link claimPassageArrival}, que também limpa a
 *    URL de novo, por garantia, e apaga o que ficou no `sessionStorage`.
 * 5. {@link consumePassageToken} troca o token curto por uma sessão em
 *    `POST /api/Passage/Consume`. A resposta traz `accessToken` (o JWT da
 *    sessão), que é gravado no mesmo lugar de sempre: `useToken().setToken`,
 *    que persiste em `localStorage['token']`.
 * 6. Se o token venceu, já foi usado ou a chamada
 *    falhou, a sessão não é criada e o usuário vai para o login normal.
 * 7. Formato antigo: enquanto o portal ainda manda o JWT da sessão em `data`,
 *    um `data` com cara de JWT vira a sessão direto, como sempre foi, sem
 *    passar pela API. Sempre aceito, sem flag.
 * 8. Saída: os links para o portal pedem sempre um token curto em `Issue` e o
 *    mandam em `#data=`, sem flag. Regra de falha: 401 encerra a sessão local e
 *    abre o destino sem token; 404, 405, 5xx (inclui 503), rede, CORS e timeout
 *    de 4 s usam o link de hoje, com o JWT na query, e o motivo vai para o log.
 *    É o que acontece em produção enquanto a API não tem o `Issue` (404) ou não
 *    libera a origem da loja em `PassageToken__ConsumeAllowedOrigins` (o
 *    preflight falha e o `fetch` lança `TypeError`): nunca vira 401 nem logout.
 *    Ver {@link openWithPassage}.
 *
 * Este módulo não importa nada do app de propósito: assim os testes rodam com
 * `node --test`, sem navegador, sem servidor e sem banco.
 */

/** Mensagem que o portal manda quando passa a sessão para a loja. */
export const PASSAGE_MESSAGE = 'redirect.token.lp';

/** Chave do `sessionStorage` onde o script do `index.html` guarda a chegada. */
export const PASSAGE_ARRIVAL_STORAGE_KEY = 'surfland.passage.arrival';

/**
 * Validade máxima do que ficou no `sessionStorage`. O token da API vive no
 * máximo 25 s; passado esse prazo com folga, a chegada é descartada.
 */
export const PASSAGE_ARRIVAL_MAX_AGE_MS = 60_000;

/** Caminho do endpoint, relativo à base `${VITE_API_URL}/api`. */
export const PASSAGE_CONSUME_PATH = '/Passage/Consume';

/** O que chegou do portal: a mensagem e o token curto. */
export interface PassageArrival {
    message: string;
    data: string;
}

/** Resultado da troca do token curto por uma sessão. */
export type PassageConsumeResult =
    | { ok: true; accessToken: string }
    | {
          ok: false;
          /**
           * - `rejected`: a API respondeu 401 (token vencido, reusado,
           *   desconhecido ou sessão original além do limite de 60 dias);
           * - `legacy-jwt`: chegou um JWT no formato antigo; ele nunca vai para
           *   o Consume (o fluxo antigo é decidido em `handlePassageArrival`);
           * - `invalid-response`: resposta fora do contrato;
           * - `network`: erro de rede, CORS ou qualquer outro status.
           */
          reason: 'rejected' | 'legacy-jwt' | 'invalid-response' | 'network';
      };

/** Subconjunto de `window` que o fluxo usa. Facilita os testes. */
export interface PassageWindow {
    location: { href: string };
    history: {
        state: unknown;
        replaceState: (data: unknown, unused: string, url?: string) => void;
    };
    sessionStorage?: Pick<Storage, 'getItem' | 'removeItem'>;
}

type FetchLike = (
    input: string,
    init: {
        method: string;
        headers: Record<string, string>;
        body: string;
        credentials: 'omit';
        cache: 'no-store';
        signal?: AbortSignal;
    }
) => Promise<{ status: number; json: () => Promise<unknown> }>;

/**
 * Diz se o valor tem cara de JWT (três partes separadas por ponto). O token
 * curto da API não tem ponto, então um JWT aqui só pode ser o formato antigo.
 */
export function looksLikeJwt(value: string): boolean {
    const parts = value.split('.');
    return parts.length === 3 && parts.every(part => part.length > 0);
}

/**
 * Tira `message` e `data` da query e do fragmento com `history.replaceState`,
 * sem recarregar a página e sem criar entrada nova no histórico. Os outros
 * parâmetros da query (cupom, token de reserva etc.) continuam lá.
 */
export function stripPassageFromUrl(win: PassageWindow): void {
    const url = new URL(win.location.href);
    const hash = new URLSearchParams(url.hash.replace(/^#/, ''));
    const hadPassage =
        url.searchParams.has('message') ||
        url.searchParams.has('data') ||
        hash.has('message') ||
        hash.has('data');
    if (!hadPassage) return;

    url.searchParams.delete('message');
    url.searchParams.delete('data');
    hash.delete('message');
    hash.delete('data');
    const restHash = hash.toString();
    const search = url.searchParams.toString();
    win.history.replaceState(
        win.history.state,
        '',
        `${url.pathname}${search ? `?${search}` : ''}${restHash ? `#${restHash}` : ''}`
    );
}

/**
 * Lê a chegada do portal e limpa a URL. Ordem de leitura: o que o script do
 * `index.html` guardou no `sessionStorage`, depois o fragmento, depois a
 * query. O registro do `sessionStorage` é apagado na leitura, então cada
 * chegada só é usada uma vez. Retorna `null` (sem tocar na URL) se não for
 * uma passagem.
 */
export function claimPassageArrival(
    win: PassageWindow,
    now: number = Date.now()
): PassageArrival | null {
    const stored = readStoredArrival(win, now);
    const url = new URL(win.location.href);
    const hash = new URLSearchParams(url.hash.replace(/^#/, ''));

    const message =
        stored?.message ?? hash.get('message') ?? url.searchParams.get('message');
    const data = stored?.data ?? hash.get('data') ?? url.searchParams.get('data');

    // Só mexe na URL quando é mesmo uma passagem: outras mensagens que usam
    // `message`/`data` continuam intactas para quem as trata.
    if (message !== PASSAGE_MESSAGE || !data) return null;

    stripPassageFromUrl(win);
    return { message, data };
}

function readStoredArrival(win: PassageWindow, now: number): PassageArrival | null {
    const storage = win.sessionStorage;
    if (!storage) return null;
    try {
        const raw = storage.getItem(PASSAGE_ARRIVAL_STORAGE_KEY);
        if (!raw) return null;
        storage.removeItem(PASSAGE_ARRIVAL_STORAGE_KEY);
        const parsed = JSON.parse(raw) as { message?: unknown; data?: unknown; at?: unknown };
        if (typeof parsed.message !== 'string' || typeof parsed.data !== 'string') return null;
        if (typeof parsed.at === 'number' && now - parsed.at > PASSAGE_ARRIVAL_MAX_AGE_MS) return null;
        return { message: parsed.message, data: parsed.data };
    } catch {
        return null;
    }
}

/**
 * Troca o token curto por uma sessão.
 *
 * Contrato da API (`PassageController.Consume`, anônimo):
 * - request: `POST {apiBaseUrl}/Passage/Consume`, corpo `{ "token": "<token curto>" }`;
 * - 200: `{ "accessToken": "<JWT da sessão>", "clientId": <número> }`;
 * - 401: `{ "accepted": false }` para token vencido, reusado, desconhecido ou
 *   sessão original fora do limite. A API não diz qual foi o caso, de propósito.
 *
 * Não manda `Authorization` nem cookies: o endpoint é anônimo e uma sessão
 * velha guardada não deve influenciar a troca. Nunca registra o token em log.
 */
export async function consumePassageToken(
    apiBaseUrl: string,
    token: string,
    fetchImpl: FetchLike = fetch as unknown as FetchLike
): Promise<PassageConsumeResult> {
    if (looksLikeJwt(token)) return { ok: false, reason: 'legacy-jwt' };

    try {
        const response = await fetchImpl(`${apiBaseUrl.replace(/\/+$/, '')}${PASSAGE_CONSUME_PATH}`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ token }),
            credentials: 'omit',
            cache: 'no-store',
        });

        if (response.status === 401) return { ok: false, reason: 'rejected' };
        if (response.status !== 200) return { ok: false, reason: 'network' };

        const body = (await response.json()) as { accessToken?: unknown } | null;
        const accessToken = body?.accessToken;
        if (typeof accessToken !== 'string' || !looksLikeJwt(accessToken)) {
            return { ok: false, reason: 'invalid-response' };
        }
        return { ok: true, accessToken };
    } catch {
        return { ok: false, reason: 'network' };
    }
}

/** Ações do app que o fluxo precisa. Injetadas para o fluxo ser testável. */
export interface PassageSessionHandlers {
    /** Grava a sessão onde o app sempre gravou. */
    saveSession: (accessToken: string) => void;
    /** Leva o usuário para o login normal. */
    openLogin: () => void;
}

/**
 * Fluxo completo da chegada: lê e limpa a URL, troca o token e grava a sessão
 * ou manda para o login. Retorna `false` quando a página não veio de uma
 * passagem (nada a fazer). Um `data` com cara de JWT segue o fluxo antigo:
 * vira a sessão sem chamar a API.
 */
export async function handlePassageArrival(
    win: PassageWindow,
    apiBaseUrl: string,
    handlers: PassageSessionHandlers,
    fetchImpl?: FetchLike
): Promise<boolean> {
    const arrival = claimPassageArrival(win);
    if (!arrival) return false;

    // Formato antigo: o portal ainda manda o JWT da sessão. Ele vira a sessão
    // como sempre foi (o app valida depois com a API). O JWT nunca é enviado
    // para o Consume.
    if (looksLikeJwt(arrival.data)) {
        handlers.saveSession(arrival.data);
        return true;
    }

    const result = await consumePassageToken(apiBaseUrl, arrival.data, fetchImpl);
    if (result.ok) handlers.saveSession(result.accessToken);
    else handlers.openLogin();
    return true;
}

// ---------------------------------------------------------------------------
// Saída: da loja para outro site (portal) com token de passagem.
// ---------------------------------------------------------------------------

/** Caminho do endpoint que emite o token curto, relativo à base da API. */
export const PASSAGE_ISSUE_PATH = '/Passage/Issue';

/** Tempo máximo de espera pelo `Issue`: 4 s, o mesmo do portal (#59). */
export const PASSAGE_ISSUE_TIMEOUT_MS = 4000;

/**
 * Resultado da emissão do token curto, com a mesma divisão do portal (#59,
 * `PassageTokenService.issue`):
 *
 * - `issued`: 200 com um token curto;
 * - `unauthorized`: 401. A sessão local venceu ou passou do limite de 60 dias.
 *   Não cai no link antigo: a sessão local é encerrada e o destino abre sem
 *   token;
 * - `fallback`: a API ainda não tem o `Issue` ou não respondeu: 404, 405, 5xx
 *   (inclui o 503 de memória cheia), erro de rede, CORS ou timeout. No `fetch`,
 *   erro de CORS e erro de rede chegam iguais (`TypeError`), por isso os dois
 *   viram `network`. API sem as chaves `PassageToken` (produção hoje) cai aqui:
 *   sem o `Issue` é 404; com o `Issue` e sem origem liberada, o preflight falha;
 * - `stopped`: qualquer outra resposta (400, 403, 200 sem token ou com cara
 *   de JWT). O destino abre sem token.
 *
 * `reason` é o que vai para o log: `http-<status>`, `network`, `timeout` ou
 * `invalid-response`. Nunca leva o token curto nem o JWT.
 */
export type PassageIssueResult =
    | { status: 'issued'; token: string }
    | { status: 'unauthorized'; reason: string }
    | { status: 'fallback'; reason: string }
    | { status: 'stopped'; reason: string };

/**
 * Pede um token curto para a sessão atual.
 *
 * Contrato da API (`PassageController.Issue`, autenticado, PR #59):
 * - request: `POST {apiBaseUrl}/Passage/Issue`, `Authorization: Bearer <JWT da
 *   sessão>`, corpo vazio (`{}`). O preflight `OPTIONS` é respondido pela API
 *   para as origens de `PassageToken__ConsumeAllowedOrigins`;
 * - 200: `{ "token": "<token curto>", "expiresInSeconds": <20 a 25> }`;
 * - 401: `{ "accepted": false }`, sessão inválida ou além do limite;
 * - 503: `{ "accepted": false }`, a API está sem espaço para guardar tokens.
 *
 * O JWT vai só no cabeçalho, nunca na URL. Não manda cookies. Se a resposta
 * não vier em `timeoutMs` (padrão 4 s), o pedido é abortado
 * (`AbortController`) e o resultado é `fallback` com `timeout`.
 */
export async function issuePassageToken(
    apiBaseUrl: string,
    sessionJwt: string,
    fetchImpl: FetchLike = fetch as unknown as FetchLike,
    timeoutMs: number = PASSAGE_ISSUE_TIMEOUT_MS
): Promise<PassageIssueResult> {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let timedOut = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // A corrida garante a resposta no prazo mesmo se o fetch ignorar o abort.
    const deadline = new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
            timedOut = true;
            controller?.abort();
            reject(new Error('timeout'));
        }, timeoutMs);
    });

    const request = async (): Promise<PassageIssueResult> => {
        const response = await fetchImpl(`${apiBaseUrl.replace(/\/+$/, '')}${PASSAGE_ISSUE_PATH}`, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${sessionJwt}`,
            },
            body: '{}',
            credentials: 'omit',
            cache: 'no-store',
            signal: controller?.signal,
        });

        const reason = `http-${response.status}`;
        if (response.status === 401) return { status: 'unauthorized', reason };
        if (response.status === 404 || response.status === 405 || response.status >= 500) {
            return { status: 'fallback', reason };
        }
        if (response.status !== 200) return { status: 'stopped', reason };

        const body = (await response.json()) as { token?: unknown } | null;
        const token = body?.token;
        // Um JWT nunca vai no fragmento: só o token curto, que não tem ponto.
        if (typeof token !== 'string' || token.length === 0 || looksLikeJwt(token)) {
            return { status: 'stopped', reason: 'invalid-response' };
        }
        return { status: 'issued', token };
    };

    try {
        return await Promise.race([request(), deadline]);
    } catch {
        return { status: 'fallback', reason: timedOut ? 'timeout' : 'network' };
    } finally {
        clearTimeout(timer);
    }
}

/**
 * Monta a URL de destino com o token curto no fragmento:
 * `<destino>#message=redirect.token.lp&data=<token curto>`, o mesmo formato
 * que o portal usa na saída. O fragmento não vai para o servidor, nem para o
 * Referer. Caminho e query do destino são mantidos; um fragmento anterior é
 * trocado.
 */
export function passageUrl(targetUrl: string, token: string): string {
    const base = targetUrl.split('#')[0];
    return `${base}#message=${encodeURIComponent(PASSAGE_MESSAGE)}&data=${encodeURIComponent(token)}`;
}

/**
 * Registra um evento da saída. Recebe só o nome do evento e o motivo
 * (`http-404`, `network`, `timeout`...), nunca o token nem o JWT.
 */
export type PassageLog = (event: string, reason: string) => void;

/**
 * Log padrão: `console.warn`, com o mesmo texto do portal
 * (`passage issue fallback <motivo>`), para dar para contar no navegador e no
 * monitoramento quantas saídas caem no fallback.
 */
export const defaultPassageLog: PassageLog = (event, reason) => {
    try {
        console.warn(event, reason);
    } catch {
        // Sem console: segue sem log.
    }
};

/** Aba aberta para o destino. Subconjunto de `Window`, para os testes. */
export interface PassagePopup {
    closed: boolean;
    opener: unknown;
    location: { href: string };
}

/** Subconjunto de `window` usado na saída. */
export interface PassageOpenerWindow {
    open: (url: string, target: string) => PassagePopup | null;
    location: { assign: (url: string) => void };
}

/** Opções de {@link openWithPassage}. */
export interface OpenWithPassageOptions {
    /** Base da API, a mesma do resto do app. */
    apiBaseUrl: string;
    /** JWT da sessão atual. Vai só no cabeçalho do `Issue`. */
    sessionJwt: string;
    /** Destino sem token. */
    targetUrl: string;
    /** O link de hoje, com o JWT na query. Usado no fallback. */
    legacyUrl: string;
    /**
     * Encerra a sessão local. Chamada quando o `Issue` responde 401. Se lançar
     * erro, o erro é registrado (sem detalhes) e a aba vai para o destino sem
     * token do mesmo jeito.
     */
    onUnauthorized: () => void;
    /** Prazo do `Issue`. Padrão {@link PASSAGE_ISSUE_TIMEOUT_MS}. */
    timeoutMs?: number;
    /** Padrão {@link defaultPassageLog}. */
    log?: PassageLog;
}

/** Como a navegação terminou. */
export type PassageOpenOutcome = 'token' | 'fallback' | 'unauthorized' | 'stopped';

/**
 * Abre o destino numa aba nova levando a sessão.
 *
 * Sempre tenta o token curto (sem flag):
 *
 * 1. Abre `about:blank` na hora do clique (depois do `await` o navegador
 *    bloquearia o pop-up) e corta o `opener`, para a aba nova não controlar
 *    esta página.
 * 2. Pede o token em `Issue` com o JWT da sessão no cabeçalho, com prazo de 4 s.
 * 3. `issued`: manda a aba para `passageUrl(destino, token)`.
 * 4. `unauthorized` (401): encerra a sessão local (`onUnauthorized`) e manda a
 *    aba para o destino sem token. Não usa o link com o JWT: a sessão venceu.
 * 5. `fallback` (404, 405, 5xx, rede, CORS, timeout): registra o motivo e manda
 *    a aba para `legacyUrl`, o link de hoje, com o JWT. Assim nada quebra se
 *    o front for ao ar antes da API, se a API cair ou se ela ainda não tiver
 *    as chaves `PassageToken` (sem origem liberada, o CORS falha). Nenhum
 *    desses casos chama `onUnauthorized`.
 * 6. `stopped` (outra resposta): registra o motivo e manda para o destino sem
 *    token. O portal fecha a aba nesse caso; aqui ela abre o destino, para o
 *    clique não terminar em nada.
 *
 * Em todos os casos a aba sai do `about:blank`, inclusive se algo der erro no
 * meio. Se o pop-up foi bloqueado, navega na própria aba com `location.assign`.
 */
export async function openWithPassage(
    win: PassageOpenerWindow,
    options: OpenWithPassageOptions,
    fetchImpl?: FetchLike
): Promise<{ outcome: PassageOpenOutcome; reason?: string }> {
    const popup = win.open('about:blank', '_blank');
    if (popup) {
        try {
            popup.opener = null;
        } catch {
            // Alguns navegadores não deixam mexer no opener; segue assim mesmo.
        }
    }

    const log = options.log ?? defaultPassageLog;
    const safeLog = (event: string, reason: string) => {
        try {
            log(event, reason);
        } catch {
            // Log não pode impedir a navegação.
        }
    };

    let destination = options.targetUrl;
    let result: { outcome: PassageOpenOutcome; reason?: string } = { outcome: 'stopped', reason: 'error' };
    try {
        const issued = await issuePassageToken(options.apiBaseUrl, options.sessionJwt, fetchImpl, options.timeoutMs);
        if (issued.status === 'issued') {
            destination = passageUrl(options.targetUrl, issued.token);
            result = { outcome: 'token' };
        } else if (issued.status === 'unauthorized') {
            safeLog('passage issue unauthorized', issued.reason);
            result = { outcome: 'unauthorized', reason: issued.reason };
            try {
                options.onUnauthorized();
            } catch {
                // O logout falhou: registra só que falhou (sem o erro, que pode
                // trazer dado da sessão) e segue para o destino sem token.
                safeLog('passage issue logout failed', 'on-unauthorized-error');
            }
        } else if (issued.status === 'fallback') {
            safeLog('passage issue fallback', issued.reason);
            destination = options.legacyUrl;
            result = { outcome: 'fallback', reason: issued.reason };
        } else {
            safeLog('passage issue stopped', issued.reason);
            result = { outcome: 'stopped', reason: issued.reason };
        }
    } finally {
        // Nunca deixa a aba em branco.
        if (popup && !popup.closed) popup.location.href = destination;
        else win.location.assign(destination);
    }
    return result;
}

/**
 * Diz se uma falha de sessão é de um token que já foi trocado. Se a sessão
 * guardada agora é outra (por exemplo, a que acabou de chegar do portal), a
 * falha é de um pedido atrasado feito com a sessão antiga e não deve apagar
 * nada. Sem sessão guardada, ou com a mesma, a falha vale e a sessão sai.
 */
export function isStaleSessionFailure(failedToken: string | null, storedToken: string | null): boolean {
    return !!failedToken && !!storedToken && failedToken !== storedToken;
}

/** Tira o token de um cabeçalho `Authorization: Bearer <token>`; senão `null`. */
export function sessionTokenFromAuthorization(header: unknown): string | null {
    if (typeof header !== 'string' || !header.startsWith('Bearer ')) return null;
    return header.slice('Bearer '.length) || null;
}
